import { z } from 'zod/v4';

import { CurrencyRuntype, MonetaryValueBackendRuntype, OfferOriginEnum, QuantityRuntype } from '../backendTypes';
import {
    CustomComponentLiteRuntype,
    CustomPartLiteRuntype,
    LifecycleEnum,
    OtsComponentLiteRuntype,
    OtsPartLiteRuntype,
} from '../part';
import { PcbPanelSpecificationRuntype } from '../pcb';
import { RegionsEnumRuntype } from '../supplierAndStockLocation';
import { PackagingRuntype } from './Packaging';

export enum PriceType {
    ListPrice = 'ListPrice',
    ContractPrice = 'ContractPrice',
    QuotePrice = 'QuotePrice',
    CustomerNegotiatedPrice = 'CustomerNegotiatedPrice',
    PurchasePrice = 'PurchasePrice',
    StandardPrice = 'StandardPrice',
    TargetPrice = 'TargetPrice',
}

export const PriceTypeRuntype = z.enum(PriceType);

export enum CustomPartPricingMode {
    Panel = 'Panel',
    Unit = 'Unit',
}
export const CustomPartPricingModeRuntype = z.enum(CustomPartPricingMode);

export enum CustomPartOfferDeliveryMethod {
    AirFreight = 'air_freight',
    SeaFreight = 'sea_freight',
}
export const CustomPartOfferDeliveryMethodRuntype = z.enum(CustomPartOfferDeliveryMethod);

export const CustomPartOfferDeliveryRuntype = z.object({
    method: CustomPartOfferDeliveryMethodRuntype,
    days: z.number(),
});
export type CustomPartOfferDeliveryDTO = z.infer<typeof CustomPartOfferDeliveryRuntype>;

export enum ItemClass {
    Standard = 'Standard',
    NonStandard = 'Non-standard',
}

export const ItemClassEnumRunType = z.enum(ItemClass);

const OneTimeCostPriceDTORuntype = z.object({
    amount: z.string(),
    currency: CurrencyRuntype,
});

export interface OneTimeCostDTO extends z.infer<typeof OneTimeCostDTORuntype> {}

export const OneTimeCostDTORuntype = z.object({
    description: z.null().or(z.string()),
    price: OneTimeCostPriceDTORuntype,
});

export type OfferPaymentTerms = z.infer<typeof OfferPaymentTermsRuntype>;
export const OfferPaymentTermsRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('net_payment'),
        net_payment_due_days: z.number(),
    }),
    z.object({
        type: z.literal('net_with_early_payment'),
        net_payment_due_days: z.number(),
        early_payment_due_days: z.number(),
        /** "0.3" means 0.3%. */
        early_payment_discount: z.string(),
    }),
] as const);

export interface OrbweaverOrigin extends z.infer<typeof OrbweaverOriginRuntype> {}

const OrbweaverOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.Orbweaver),
    id: z.string(),
    datasource_name: z.string(),
});
const ManualOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.Manual),
});

const WuerthOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.Wuerth),
});

const BetaLayoutOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.BetaLayout),
});

const QuoteRequestOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.QuoteRequest),
    quote_request_id: z.string().nullable(),
    quote_request_line_item_id: z.number().nullable(),
    negotiation_id: z.number().nullable(),
});

export interface ImportOrigin extends z.infer<typeof ImportOriginRuntype> {}

const ImportOriginRuntype = z.object({
    origin: z.literal(OfferOriginEnum.Import),
    imported_by: z.string().nullable().optional(),
    // The free-text `context` was removed from the origin in INT-1204. The
    // resolved import-context name now arrives as a sibling on the offer response
    // (`import_context_name` on StandardPartOfferResponse). INT-1767 would move it
    // back onto the origin as a proper `context_name` field.
    raw_mpn: z.string().nullable().optional(),
    raw_manufacturer: z.string().nullable().optional(),
});

export const OffTheShelfOfferOriginEnumRuntype = z.enum([
    OfferOriginEnum.AlElektronik,
    OfferOriginEnum.Alfatec,
    OfferOriginEnum.Arrow,
    OfferOriginEnum.AvnetAbacus,
    OfferOriginEnum.AvnetApac,
    OfferOriginEnum.AvnetEbv,
    OfferOriginEnum.AvnetSilica,
    OfferOriginEnum.AvnetUsa,
    OfferOriginEnum.Blume,
    OfferOriginEnum.Bossard,
    OfferOriginEnum.Buerklin,
    OfferOriginEnum.ChipCart,
    OfferOriginEnum.Corestaff,
    OfferOriginEnum.Digikey,
    OfferOriginEnum.Ecomal,
    OfferOriginEnum.Element14,
    OfferOriginEnum.Eve,
    OfferOriginEnum.Farnell,
    OfferOriginEnum.Future,
    OfferOriginEnum.Greenchips,
    OfferOriginEnum.Gudeco,
    OfferOriginEnum.Heilind,
    OfferOriginEnum.Hexachip,
    OfferOriginEnum.IcHub,
    OfferOriginEnum.Kruse,
    OfferOriginEnum.Ineltro,
    OfferOriginEnum.AtlantikNetworxx,
    OfferOriginEnum.Mev,
    OfferOriginEnum.Lcsc,
    OfferOriginEnum.Master,
    OfferOriginEnum.Mouser,
    OfferOriginEnum.MyArrow,
    OfferOriginEnum.Newark,
    OfferOriginEnum.Octopart,
    OfferOriginEnum.OnlineComponents,
    OfferOriginEnum.QuestComponents,
    OfferOriginEnum.RapidElectronics,
    OfferOriginEnum.RmComponents,
    OfferOriginEnum.Rochester,
    OfferOriginEnum.RsComponents,
    OfferOriginEnum.Rutronik,
    OfferOriginEnum.Samtec,
    OfferOriginEnum.Schukat,
    OfferOriginEnum.Sos,
    OfferOriginEnum.Sourcengine,
    OfferOriginEnum.Stackrate,
    OfferOriginEnum.Telcona,
    OfferOriginEnum.Ti,
    OfferOriginEnum.Tme,
    OfferOriginEnum.TrustedParts,
    OfferOriginEnum.Tti,
    OfferOriginEnum.Unikey,
    OfferOriginEnum.Venkel,
    OfferOriginEnum.Weltron,
    OfferOriginEnum.WinSource,
    OfferOriginEnum.Wittig,
    OfferOriginEnum.WuerthEisos,
]);

export type OffTheShelfOfferOrigin = z.infer<typeof OffTheShelfOfferOriginEnumRuntype>;

export const DirectApiOriginEnumRuntype = z.enum([
    OfferOriginEnum.APCT,
    OfferOriginEnum.AlElektronik,
    OfferOriginEnum.Alba,
    OfferOriginEnum.Alfatec,
    OfferOriginEnum.Arrow,
    OfferOriginEnum.AvnetAbacus,
    OfferOriginEnum.AvnetApac,
    OfferOriginEnum.AvnetEbv,
    OfferOriginEnum.AvnetSilica,
    OfferOriginEnum.AvnetUsa,
    OfferOriginEnum.Blume,
    OfferOriginEnum.Bossard,
    OfferOriginEnum.Buerklin,
    OfferOriginEnum.ChipCart,
    OfferOriginEnum.Consigned,
    OfferOriginEnum.Corestaff,
    OfferOriginEnum.Digikey,
    OfferOriginEnum.Ecomal,
    OfferOriginEnum.Element14,
    OfferOriginEnum.Eve,
    OfferOriginEnum.Farnell,
    OfferOriginEnum.Future,
    OfferOriginEnum.Gatema,
    OfferOriginEnum.Greenchips,
    OfferOriginEnum.Gudeco,
    OfferOriginEnum.Heilind,
    OfferOriginEnum.Hexachip,
    OfferOriginEnum.IBRRingler,
    OfferOriginEnum.IcHub,
    OfferOriginEnum.Kruse,
    OfferOriginEnum.Ineltro,
    OfferOriginEnum.AtlantikNetworxx,
    OfferOriginEnum.Mev,
    OfferOriginEnum.Lcsc,
    OfferOriginEnum.Master,
    OfferOriginEnum.Mouser,
    OfferOriginEnum.MultiCB,
    OfferOriginEnum.MyArrow,
    OfferOriginEnum.Newark,
    OfferOriginEnum.Octopart,
    OfferOriginEnum.OnlineComponents,
    OfferOriginEnum.QuestComponents,
    OfferOriginEnum.RapidElectronics,
    OfferOriginEnum.RmComponents,
    OfferOriginEnum.Rochester,
    OfferOriginEnum.RsComponents,
    OfferOriginEnum.Rutronik,
    OfferOriginEnum.SafePcb,
    OfferOriginEnum.Samtec,
    OfferOriginEnum.Schukat,
    OfferOriginEnum.SiliconExpert,
    OfferOriginEnum.Sos,
    OfferOriginEnum.Sourcengine,
    OfferOriginEnum.Stackrate,
    OfferOriginEnum.Telcona,
    OfferOriginEnum.Ti,
    OfferOriginEnum.Tme,
    OfferOriginEnum.TrustedParts,
    OfferOriginEnum.Tti,
    OfferOriginEnum.Unikey,
    OfferOriginEnum.Venkel,
    OfferOriginEnum.Weltron,
    OfferOriginEnum.WinSource,
    OfferOriginEnum.Wittig,
    OfferOriginEnum.WuerthEisos,
]);

const DirectApiOriginRuntype = z.object({
    origin: DirectApiOriginEnumRuntype,
    id: z.string().optional(),
});

export type AllOrigins = z.infer<typeof AllOriginRuntype>;
export const AllOriginRuntype = z.union([
    ImportOriginRuntype,
    DirectApiOriginRuntype,
    OrbweaverOriginRuntype,
    ManualOriginRuntype,
    WuerthOriginRuntype,
    BetaLayoutOriginRuntype,
    QuoteRequestOriginRuntype,
]);

/** The PartLite kinds an offer can link: no Generic, RawSpecification or Unknown. */
export type SourceablePartLite = z.infer<typeof SourceablePartLiteRuntype>;
export const SourceablePartLiteRuntype = z.union([
    OtsPartLiteRuntype,
    OtsComponentLiteRuntype,
    CustomPartLiteRuntype,
    CustomComponentLiteRuntype,
]);

// Standard part offers can only link off-the-shelf parts or IPNs.
export type StandardOfferLinkedPart = z.infer<typeof StandardOfferLinkedPartRuntype>;
export const StandardOfferLinkedPartRuntype = z.discriminatedUnion('kind', [
    OtsPartLiteRuntype,
    OtsComponentLiteRuntype,
]);

// Custom part offers can only link custom parts or custom components.
export type CustomOfferLinkedPart = z.infer<typeof CustomOfferLinkedPartRuntype>;
export const CustomOfferLinkedPartRuntype = z.discriminatedUnion('kind', [
    CustomPartLiteRuntype,
    CustomComponentLiteRuntype,
]);

export enum OfferLinkedLocationType {
    SupplierAndStockLocation = 'SupplierAndStockLocation',
    InventorySite = 'InventorySite',
}

export type SupplierLocation = z.infer<typeof SupplierLocationRuntype>;
export const SupplierLocationRuntype = z.object({
    type: z.literal(OfferLinkedLocationType.SupplierAndStockLocation),
    id: z.string(),
    supplier: z.object({
        id: z.string(),
        name: z.string(),
    }),
    number: z.string().nullable(),
    stock_location: RegionsEnumRuntype,
});

const InventorySiteLocationRuntype = z.object({
    type: z.literal(OfferLinkedLocationType.InventorySite),
    id: z.string(),
    name: z.string(),
    number: z.string().nullable(),
});

export type OfferLinkedLocation = z.infer<typeof OfferLinkedLocationRuntype>;
export const OfferLinkedLocationRuntype = z.discriminatedUnion('type', [
    SupplierLocationRuntype,
    InventorySiteLocationRuntype,
]);

export type StandardPartPriceBreak = z.infer<typeof StandardPartPriceBreakRuntype>;
export const StandardPartPriceBreakRuntype = z.object({
    minimum_order_quantity: z.number(),
    minimum_packaging_quantity: z.number(),
    unit_price: MonetaryValueBackendRuntype,
});

export type CustomPartPriceBreak = z.infer<typeof CustomPartPriceBreakRuntype>;
export const CustomPartPriceBreakRuntype = z.object({
    minimum_order_quantity: z.number(),
    minimum_packaging_quantity: z.number(),
    unit_price: MonetaryValueBackendRuntype,
    lead_time_in_days: z.number().nullish(),
    description: z.string().nullable(),
});

const OnOrderResponseRuntype = z.object({
    quantity: z.number(),
    date: z.string().nullable(),
});

export type StandardPartAvailability = z.infer<typeof StandardPartAvailabilityRuntype>;
export const StandardPartAvailabilityRuntype = z.object({
    stock: z.number().nullable(),
    total_stock: z.number().nullable(),
    factory_lead_time_days: z.number().nullable(),
    factory_quantity: z.number().nullable(),
    on_order: z.array(OnOrderResponseRuntype),
});

const OfferResponseBaseFields = {
    id: z.string(),
    linked_location: OfferLinkedLocationRuntype,
    origin: AllOriginRuntype,
    creation_date: z.string(),
    unit_of_measurement: QuantityRuntype,
    one_time_costs: z.array(OneTimeCostDTORuntype),
    offer_number: z.string().nullable(),
    offer_url: z.string().nullable(),
    supplier_part_number: z.string().nullable(),
    valid_from: z.string().nullable(),
    valid_until: z.string().nullable(),
    payment_terms: OfferPaymentTermsRuntype.nullable(),
    cancellation_window_days: z.number().nullable(),
    item_class: ItemClassEnumRunType.nullable(),
    restricted_for: z.array(
        z.discriminatedUnion('type', [
            z.object({
                type: z.literal('Customer'),
                data: z.object({ id: z.uuid(), name: z.string(), number: z.string().nullable() }),
            }),
            z.object({
                type: z.literal('RfQ'),
                data: z.object({ id: z.uuid(), name: z.string(), number: z.string().nullable() }),
            }),
            z.object({
                type: z.literal('Negotiation'),
                data: z.object({ id: z.uuid(), name: z.string() }),
            }),
            z.object({
                type: z.literal('ShipToSite'),
                data: z.object({ id: z.uuid(), name: z.string() }),
            }),
        ] as const),
    ),
    notes: z.string().nullable(),
    attachment: z.string().nullable(),
};

export enum OfferKind {
    StandardPart = 'StandardPart',
    CustomPart = 'CustomPart',
}

export type StandardPartOfferResponseDTO = z.infer<typeof StandardPartOfferResponseRuntype>;
export const StandardPartOfferResponseRuntype = z.object({
    ...OfferResponseBaseFields,
    kind: z.literal(OfferKind.StandardPart),
    linked_part: StandardOfferLinkedPartRuntype,
    // Resolved name of the offer's import context, when it has one. Carried as a
    // sibling of `origin` (not inside it) because INT-1204 removed the free-text
    // `context` from the wire `OfferOrigin`; the backend resolves the name from
    // `import_context_id` on the response. Read it via `formatOfferOriginWithContext`.
    // INT-1767 would fold this back into the origin wire DTO.
    import_context_name: z.string().nullable().optional(),
    price_type: PriceTypeRuntype,
    price_breaks: z.array(StandardPartPriceBreakRuntype),
    availability: StandardPartAvailabilityRuntype,
    packaging: PackagingRuntype.nullable(),
    ncnr: z.boolean().nullable(),
});

export type CustomOptionOfferResponseDTO = z.infer<typeof CustomOptionOfferResponseRuntype>;
export const CustomOptionOfferResponseRuntype = z.object({
    ...OfferResponseBaseFields,
    kind: z.literal(OfferKind.CustomPart),
    linked_part: CustomOfferLinkedPartRuntype,
    price_type: PriceTypeRuntype.nullable(),
    price_breaks: z.array(CustomPartPriceBreakRuntype),
    pricing_mode: CustomPartPricingModeRuntype.nullable(),
    specification: PcbPanelSpecificationRuntype.nullable(),
    sourcing_batch_size: z.number().nullable(),
    total_offered_quantity: z.number().nullable(),
    has_order_files: z.boolean(),
    lifecycle: z.enum(LifecycleEnum).nullable().optional(),
    // The supplier-specific custom part this offer is for. Use it to fetch the
    // CustomPart's own compliance/lifecycle rather than the specification's.
    custom_part_id: z.string().nullable().optional(),
    delivery: CustomPartOfferDeliveryRuntype.nullable().optional(),
});

export type OfferResponseDTO = z.infer<typeof OfferResponseRuntype>;
export const OfferResponseRuntype = z.discriminatedUnion('kind', [
    StandardPartOfferResponseRuntype,
    CustomOptionOfferResponseRuntype,
]);
